import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faXmark, faSpinner, faRotateRight, faLock
} from '@fortawesome/free-solid-svg-icons';

interface ActionOtpModalProps {
  /** เปิด/ปิด modal */
  isOpen: boolean;
  /** เรียกเมื่อผู้ใช้ปิด modal */
  onClose: () => void;
  /**
   * เรียกเมื่อ OTP ผ่านแล้ว — รับ action_token (valid 5 นาที)
   * ให้นำ action_token ใส่ใน body ของ API call จริง
   */
  onVerified: (actionToken: string) => void | Promise<void>;
  /** action key ต้องตรงกับ requireActionToken('...') ใน backend */
  action: string;
  /** หัวข้อที่แสดงบน modal */
  title?: string;
  /** คำอธิบายการดำเนินการ */
  description?: string;
}

const OTP_LENGTH = 6;

const ActionOtpModal: React.FC<ActionOtpModalProps> = ({
  isOpen,
  onClose,
  onVerified,
  action,
  title = 'ยืนยันการดำเนินการ',
  description = 'กรุณากรอกรหัส OTP ที่ส่งไปยังอีเมลของคุณ'
}) => {
  const [otp, setOtp] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = (seconds: number) => {
    setCooldown(seconds);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const requestOtp = async () => {
    setRequesting(true);
    try {
      const res = await merchantApi.post('/merchants/wallet/action-otp/request', { action });
      setEmailHint(res.data.data?.email_hint || '');
      startCooldown(res.data.data?.cooldown_seconds || 60);
    } catch (err: any) {
      const cd = err.response?.data?.data?.cooldown_seconds;
      if (cd) {
        startCooldown(cd);
        toast.error(`กรุณารอ ${cd} วินาที`);
      } else {
        toast.error(err.response?.data?.message || 'เกิดข้อผิดพลาดในการส่ง OTP');
        onClose();
      }
    } finally {
      setRequesting(false);
    }
  };

  // Auto-request OTP เมื่อ modal เปิด
  useEffect(() => {
    if (isOpen) {
      setOtp('');
      setCooldown(0);
      requestOtp();
      setTimeout(() => inputRef.current?.focus(), 300);
    } else {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    }
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, [isOpen]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifying(true);
    try {
      const res = await merchantApi.post('/merchants/wallet/action-otp/verify', { action, otp });
      const actionToken: string = res.data.data?.action_token;
      setOtp('');
      onClose();
      onVerified(actionToken);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'รหัส OTP ไม่ถูกต้อง');
    } finally {
      setVerifying(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
              <FontAwesomeIcon icon={faShieldAlt} className="text-blue-500" />
            </div>
            <h2 className="font-bold text-gray-800 text-base">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 transition-colors"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {requesting ? (
            <div className="text-center py-8">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl text-blue-500 mb-3" />
              <p className="text-gray-500 text-sm">กำลังส่ง OTP ไปยังอีเมล...</p>
            </div>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="text-center text-sm text-gray-500 mb-2">
                <p>{description}</p>
                {emailHint && (
                  <p className="mt-1">
                    ส่งไปยัง{' '}
                    <span className="font-medium text-blue-600">{emailHint}</span>
                  </p>
                )}
              </div>

              {/* OTP Input */}
              <div>
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
                  maxLength={OTP_LENGTH}
                  placeholder="000000"
                  className="w-full px-4 py-4 rounded-xl text-center text-3xl font-bold tracking-[0.5rem]
                             border-2 border-gray-200 focus:border-blue-500 focus:outline-none
                             transition-colors placeholder-gray-300"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                />
              </div>

              {/* Verify button */}
              <button
                type="submit"
                disabled={otp.length !== OTP_LENGTH || verifying}
                className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-all
                           hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed
                           disabled:hover:translate-y-0 flex items-center justify-center gap-2"
                style={{ background: 'var(--theme-primary-gradient)' }}
              >
                {verifying
                  ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin" />กำลังตรวจสอบ...</>
                  : <><FontAwesomeIcon icon={faLock} />ยืนยัน</>
                }
              </button>

              {/* Resend */}
              <button
                type="button"
                onClick={requestOtp}
                disabled={cooldown > 0 || requesting}
                className="w-full py-2.5 rounded-xl border border-gray-200 text-gray-500 text-sm
                           transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                           flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faRotateRight} className={requesting ? 'animate-spin' : ''} />
                {cooldown > 0 ? `ส่งรหัสใหม่ได้ใน ${cooldown} วินาที` : 'ส่งรหัสใหม่'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ActionOtpModal;
